import { useMemo } from "react";
import { observer } from "mobx-react-lite";
import { useNavigate } from "react-router-dom";
import { MoreHorizontal, Settings, Trash2 } from "lucide-react";
import { Can } from "@/hooks/useCan/Can";
import { useAuthStore } from "@/features/auth";
import { useTran } from "@/hooks";
import { PATHS } from "@/lib/router";
import { DataTable } from "@/components/common";
import { Button } from "@/components/ui/button";
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuLabel,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from "@/components/ui/dropdown-menu";
import { useCarbonTargetsStore } from "../../hooks";

const TargetSettingsTableSection = observer(() => {
  const { t } = useTran();
  const navigate = useNavigate();
  const { user } = useAuthStore();
  const permissions = useMemo(
    () => user?.permissions || [],
    [user?.permissions],
  );
  const store = useCarbonTargetsStore();

  const columns = useMemo(
    () => [
      {
        key: "targetYear",
        header: t("Target Year"),
        sortable: true,
        showSortIndicators: true,
        render: (row) => row.year,
      },
      {
        key: "scope1",
        header: t("Scope 1 total (tCO₂e)"),
        sortable: true,
        showSortIndicators: true,
        render: (row) => row.scope1?.total || 0,
      },
      {
        key: "scope2",
        header: t("Scope 2 total (kWh)"),
        sortable: true,
        showSortIndicators: true,
        render: (row) => row.scope2?.total || 0,
      },
      {
        key: "updatedAt",
        header: t("Upated at"),
        sortable: true,
        showSortIndicators: true,
        render: (row) => new Date(row.updatedAt).toLocaleString(),
      },
      {
        key: "actions",
        header: t("Actions"),
        align: "right",
        render: (row) => (
          <div className="flex justify-end">
            <DropdownMenu>
              <DropdownMenuTrigger asChild>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon"
                  aria-label={t("Actions")}
                >
                  <MoreHorizontal className="h-4 w-4" />
                </Button>
              </DropdownMenuTrigger>
              <DropdownMenuContent align="end" className="w-48">
                <DropdownMenuLabel>{t("Actions")}</DropdownMenuLabel>
                <Can
                  resource="carbon_target_setting"
                  action="update"
                  userPermissions={permissions}
                >
                  <DropdownMenuItem
                    className="gap-2"
                    onSelect={() => {
                      navigate(
                        PATHS.CARBON_TARGETS_EDIT.replace(":id", row._id || ""),
                      );
                    }}
                  >
                    <Settings className="h-4 w-4 text-blue-600" />
                    {t("Manage")}
                  </DropdownMenuItem>
                </Can>
                <DropdownMenuSeparator />
                <Can
                  resource="target_setting"
                  action="delete"
                  userPermissions={permissions}
                >
                  <DropdownMenuItem
                    className="gap-2 text-destructive focus:text-destructive"
                    onSelect={() => store.setDeleteTarget(row)}
                  >
                    <Trash2 className="h-4 w-4" />
                    {t("Delete")}
                  </DropdownMenuItem>
                </Can>
              </DropdownMenuContent>
            </DropdownMenu>
          </div>
        ),
      },
    ],
    [navigate, permissions, store, t],
  );
  return (
    <div className="rounded-radius border border-border bg-card p-4">
      <DataTable
        columns={columns}
        data={store.items}
        loading={store.loadingList}
        getRowKey={(row) => row?.id ?? row?._id}
        emptyMessage={t("No target settings found.")}
        pagination={{
          page: store.page,
          pageSize: store.pageSize,
          total: store.total,
          onPageChange: store.setPage,
          onPageSizeChange: store.setPageSize,
          pageSizeOptions: [10, 20, 50],
        }}
      />
    </div>
  );
});

export default TargetSettingsTableSection;
